import React, { useState } from 'react';
import meterNetwork from '../../assets/meter/meter-network.png';

// Approximate coordinates on the 1500x988 source image for each labeled
// element, used to animate signal-flow dots along the same paths the image
// already draws in static blue. The image itself is untouched, this is a
// transparent SVG layer on top of it.
const METER = { x: 750, y: 645 };
const NODES = [
  { key: 'lte', label: '4G / LTE Connectivity', pos: { x: 330, y: 265 } },
  { key: 'router', label: 'Router (Wi-Fi)', pos: { x: 260, y: 618 } },
  { key: 'grid', label: 'Grid Supply', pos: { x: 300, y: 883 } },
  { key: 'cloud', label: 'GRIDx Cloud', pos: { x: 750, y: 150 } },
  { key: 'geyser', label: 'Geyser Control', pos: { x: 1425, y: 255 } },
  { key: 'demand', label: 'Demand Management', pos: { x: 1428, y: 578 } },
  { key: 'home', label: 'Home Appliances', pos: { x: 1415, y: 895 } },
];

const STATES = [
  { key: 'normal', label: 'Normal' },
  { key: 'healthy', label: 'Healthy' },
  { key: 'alert', label: 'Alert' },
];

const SignalOverlay = ({ status }) => (
  <svg
    className={`hiw-overlay hiw-overlay--${status}`}
    viewBox="0 0 1500 988"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    {NODES.map((n, i) => (
      <line
        key={n.key}
        x1={METER.x} y1={METER.y} x2={n.pos.x} y2={n.pos.y}
        className="hiw-overlay__pulse"
        style={{ animationDelay: `${i * 0.4}s` }}
      />
    ))}
    <circle cx={METER.x} cy={METER.y} r="14" className="hiw-overlay__meter-pulse" />
  </svg>
);

const HowItWorks = () => {
  const [status, setStatus] = useState('normal');

  return (
    <section className="gh-section hiw" id="how-it-works">
      <div className="gh-section__inner">
        <span className="eyebrow">How the GRIDx meter works</span>
        <h2 className="gh-section__title">One meter. A complete energy network.</h2>
        <p className="gh-section__sub">
          The GRIDx meter sits between the grid and the home, measuring supply,
          communicating with the GRIDx Cloud, and coordinating the devices connected
          to it.
        </p>

        <div className="hiw__status" role="group" aria-label="Network status demo">
          <span className="hiw__status-label">Network status:</span>
          {STATES.map((s) => (
            <button
              key={s.key}
              className={`hiw__status-btn hiw__status-btn--${s.key} ${status === s.key ? 'is-active' : ''}`}
              onClick={() => setStatus(s.key)}
              aria-pressed={status === s.key}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="hiw__diagram">
          <img
            src={meterNetwork}
            alt="Diagram of the GRIDx smart meter connecting 4G/LTE, a Wi-Fi router, grid supply, geyser control and demand management devices, and home appliances to the GRIDx Cloud Platform"
            className="hiw__diagram-img"
          />
          <SignalOverlay status={status} />
        </div>

        <div className="hiw__legend">
          {NODES.map((n) => (
            <div key={n.key} className="hiw__legend-item">
              <span className="hiw__legend-dot" />
              {n.label}
            </div>
          ))}
        </div>

        <details className="hiw__video">
          <summary>Watch the GRIDx smart meter demo</summary>
          <div className="hiw__video-wrapper">
            <iframe
              src="https://www.youtube.com/embed/D-f2QchGfj8"
              title="GRIDx Smart Meter Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </details>
      </div>
    </section>
  );
};

export default HowItWorks;
